import { useQuery } from "@tanstack/react-query";
import { apiJson } from "@/api/client";

/* The server states the oldest app build it still accepts, so a stale sideloaded app says so. */

export type ServerVersion = {
  version: string;
  min_client_version: string;
  /** Older servers leave it out. */
  idle_timeout_minutes?: number;
};

const DEFAULT_IDLE_TIMEOUT_MINUTES = 30;

/**
 * Minutes without activity before the app signs the director out; 0 = never. Until the server
 * says (loading, failed, or an older API) it is 30, so idle sign-out fails closed.
 * shortcut: a server set above 30 can still sign out a reload idle longer than 30 minutes
 * before /version answers; remember the last served value if that is ever configured.
 */
export function idleTimeoutMinutes(version: ServerVersion | undefined): number {
  return version?.idle_timeout_minutes ?? DEFAULT_IDLE_TIMEOUT_MINUTES;
}

/** The version this build was made from; vite.config.ts fills it in. */
export const APP_VERSION: string = import.meta.env.VITE_APP_VERSION ?? "0.0.0";

function parts(version: string): number[] {
  return version.split(".").map((part) => Number.parseInt(part, 10) || 0);
}

/** True when this app is older than the minimum the server asks for. */
export function isOutdated(appVersion: string, minimum: string): boolean {
  const app = parts(appVersion);
  const needed = parts(minimum);
  for (let index = 0; index < Math.max(app.length, needed.length); index += 1) {
    const mine = app[index] ?? 0;
    const wanted = needed[index] ?? 0;
    if (mine !== wanted) return mine < wanted;
  }
  return false;
}

export function fetchServerVersion(): Promise<ServerVersion> {
  return apiJson<ServerVersion>("/version");
}

export function useServerVersion() {
  return useQuery({
    queryKey: ["version"] as const,
    queryFn: fetchServerVersion,
    staleTime: 5 * 60_000,
    retry: false,
  });
}
